import { METRIC_UNITS, IMPERIAL_UNITS } from "./schema";

export function convertToGrams(amount, unit, ingredient) {
  if (amount === 0) return 0;

  switch (unit) {
    case 'g':
      return amount;
    case 'kg':
      return amount * 1000;
    case 'mg':
      return amount / 1000;
    case 'oz':
      return amount * 28.3495;
    case 'lb':
      return amount * 453.592;
    case 'ml':
      return amount * (ingredient?.density || 1);
    case 'l':
      return amount * 1000 * (ingredient?.density || 1);
    case 'fl oz':
      return amount * 29.5735 * (ingredient?.density || 1);
    case 'pint':
      return amount * 473.176 * (ingredient?.density || 1);
    case 'quart':
      return amount * 946.353 * (ingredient?.density || 1);
    case 'gallon':
      return amount * 3785.41 * (ingredient?.density || 1);
    case 'tsp':
      return amount * 4.92892 * (ingredient?.density || 1);
    case 'tbsp':
      return amount * 14.7868 * (ingredient?.density || 1);
    case 'cup':
      return amount * 236.588 * (ingredient?.density || 1);
    case 'can':
      return amount * 355 * (ingredient?.density || 1);
    case 'none':
    case 'piece':
      return (ingredient?.piece_weight) ? amount * ingredient.piece_weight : 0;      
    case 'slice':
      return (ingredient?.slice_weight) ? amount * ingredient.slice_weight : 0;
    case 'pinch':
      return amount * 0.3;
    case 'dash':
      return amount * 0.6;
    case 'clove':
      return amount * 3;
    case 'to taste':
      return 0;     
    default:
      return 0;
  }
}

export function convertUnit(amount, unit, preferredSystem, ingredient) {
  if (!amount || amount === 0) {
    return { amount: 0, unit };
  }

  if (isTrulyCommonUnit(unit)) {
    return { amount, unit };
  }

  const isWeight = isWeightUnit(unit);
  const isVolume = isVolumeUnit(unit);
  const isNativeVolume = isNativeVolumeUnit(unit);
  const isCookingVolume = isCookingVolumeUnit(unit);
  
  if (!isWeight && !isVolume) {
    return { amount, unit };
  }

  const currentSystem = METRIC_UNITS.includes(unit) ? 'metric' : 
                        IMPERIAL_UNITS.includes(unit) ? 'imperial' : null;

  if (isCookingVolume && preferredSystem === 'imperial') {
    return { amount, unit };
  }

  if (currentSystem === preferredSystem && !isCookingVolume) {
    return { amount, unit };
  }

  if (isWeight) {
    if (!ingredient) {
      return { amount, unit };
    }
    const grams = convertToGrams(amount, unit, ingredient);
    if (grams === 0) {
      return { amount: 0, unit: preferredSystem === 'imperial' ? 'oz' : 'g' };
    }
    return selectBestWeightUnit(grams, preferredSystem, ingredient);
  }

  if (isVolume) {
    if (!ingredient || !ingredient.density) {
      return { amount, unit };
    }

    const grams = convertToGrams(amount, unit, ingredient);
    if (grams === 0) {
      return { amount: 0, unit: preferredSystem === 'imperial' ? 'oz' : 'g' };
    }

    if (isCookingVolume && preferredSystem === 'metric') {
      return selectBestWeightUnit(grams, preferredSystem, ingredient);
    }

    if (isNativeVolume) {
      return selectBestVolumeUnit(grams, preferredSystem, ingredient);
    }

    return selectBestWeightUnit(grams, preferredSystem, ingredient);
  }

  return { amount, unit };
}

function isTrulyCommonUnit(unit) {
  return ['pinch', 'piece', 'slice', 'clove', 'dash', 'to taste', 'none'].includes(unit);
}

function convertGramsToUnit(grams, targetUnit, ingredient) {
  if (grams === 0) return { amount: 0, unit: targetUnit };

  switch (targetUnit) {
    case 'g':
      return { amount: grams, unit: 'g' };
    case 'kg':
      return { amount: grams / 1000, unit: 'kg' };
    case 'mg':
      return { amount: grams * 1000, unit: 'mg' };
    case 'oz':
      return { amount: grams / 28.3495, unit: 'oz' };
    case 'lb':
      return { amount: grams / 453.592, unit: 'lb' };
    case 'ml':
      return { amount: grams / ingredient.density, unit: 'ml' };
    case 'l':
      return { amount: grams / (1000 * ingredient.density), unit: 'l' };
    case 'fl oz':
      return { amount: grams / (29.5735 * ingredient.density), unit: 'fl oz' };
    case 'pint':
      return { amount: grams / (473.176 * ingredient.density), unit: 'pint' };
    case 'quart':
      return { amount: grams / (946.353 * ingredient.density), unit: 'quart' };
    case 'gallon':
      return { amount: grams / (3785.41 * ingredient.density), unit: 'gallon' };
    case 'tsp':
      return { amount: grams / (4.92892 * ingredient.density), unit: 'tsp' };
    case 'tbsp':
      return { amount: grams / (14.7868 * ingredient.density), unit: 'tbsp' };
    case 'cup':
      return { amount: grams / (236.588 * ingredient.density), unit: 'cup' };
    default:
      return { amount: grams, unit: 'g' };
  }
}

function isWeightUnit(unit) {
  return ['g', 'kg', 'mg', 'oz', 'lb'].includes(unit);
}

function isVolumeUnit(unit) {
  return ['ml', 'l', 'fl oz', 'pint', 'quart', 'gallon', 'tsp', 'tbsp', 'cup'].includes(unit);
}

function isNativeVolumeUnit(unit) {
  return ['ml', 'l', 'fl oz', 'pint', 'quart', 'gallon'].includes(unit);
}

function isCookingVolumeUnit(unit) {
  return ['tsp', 'tbsp', 'cup'].includes(unit);
}

function getPreferredWeightUnit(preferredSystem) {
  return preferredSystem === 'imperial' ? 'oz' : 'g';
}

function getPreferredVolumeUnit(preferredSystem) {
  return preferredSystem === 'imperial' ? 'fl oz' : 'ml';
}

function selectBestVolumeUnit(grams, preferredSystem, ingredient) {
  if (grams === 0 || !ingredient || !ingredient.density) return { amount: 0, unit: 'ml' };

  const preferredVolUnit = getPreferredVolumeUnit(preferredSystem);
  const volResult = convertGramsToUnit(grams, preferredVolUnit, ingredient);
  
  if (preferredSystem === 'imperial') {
    if (volResult.amount < 0.5) {
      const tspResult = convertGramsToUnit(grams, 'tsp', ingredient);
      if (tspResult.amount >= 0.5) return tspResult;
    }
    if (volResult.amount >= 0.5 && volResult.amount < 1) {
      const tbspResult = convertGramsToUnit(grams, 'tbsp', ingredient);
      if (tbspResult.amount >= 0.5) return tbspResult;
    }
    if (volResult.amount >= 1 && volResult.amount < 8) {
      const cupResult = convertGramsToUnit(grams, 'cup', ingredient);
      if (cupResult.amount >= 0.25) return cupResult;
    }
    if (volResult.amount >= 1 && volResult.amount < 16) return { amount: volResult.amount, unit: 'fl oz' };
    if (volResult.amount >= 16 && volResult.amount < 32) return convertGramsToUnit(grams, 'pint', ingredient);
    if (volResult.amount >= 32 && volResult.amount < 128) return convertGramsToUnit(grams, 'quart', ingredient);
    if (volResult.amount >= 128) return convertGramsToUnit(grams, 'gallon', ingredient);
    return { amount: volResult.amount, unit: 'fl oz' };
  } else {
    if (volResult.amount >= 1 && volResult.amount < 1000) return { amount: volResult.amount, unit: 'ml' };
    if (volResult.amount >= 1000) return convertGramsToUnit(grams, 'l', ingredient);
    return { amount: volResult.amount, unit: 'ml' };
  }
}

function selectBestWeightUnit(grams, preferredSystem, ingredient) {
  if (grams === 0) return { amount: 0, unit: 'g' };

  const preferredWeightUnit = getPreferredWeightUnit(preferredSystem);
  let weightResult = convertGramsToUnit(grams, preferredWeightUnit, ingredient);
  
  if (preferredSystem === 'imperial') {
    if (weightResult.amount >= 16) {
      weightResult = convertGramsToUnit(grams, 'lb', ingredient);
    }
  } else {
    if (weightResult.amount >= 1000) {
      weightResult = convertGramsToUnit(grams, 'kg', ingredient);
    } else if (weightResult.amount < 1 && weightResult.amount > 0) {
      weightResult = convertGramsToUnit(grams, 'mg', ingredient);
    }
  }

  return {
    amount: roundAmount(weightResult.amount, weightResult.unit),
    unit: weightResult.unit
  };
}


function roundAmount(amount, unit) {
  if (unit === 'g' && amount >= 10) {
    return Math.round(amount);
  }
  if (unit === 'kg' && amount >= 0.01) {
    return Math.round(amount * 100) / 100;
  }
  if (unit === 'oz' && amount >= 1) {
    return Math.round(amount * 10) / 10;
  }
  if (unit === 'lb' && amount >= 0.1) {
    return Math.round(amount * 10) / 10;
  }
  if (unit === 'mg' && amount >= 100) {
    return Math.round(amount);
  }
  return amount;
}
